// web/src/pages/Login.jsx
import React, { useEffect, useState, useRef, useMemo } from "react";
import "./Login.css";
import { useNavigate } from "react-router-dom";

// ✅ Firebase Auth
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
} from "firebase/auth";

// ✅ Firestore
import { doc, getDoc } from "firebase/firestore";

import { auth, db } from "../firebase/firebase";

export default function Login() {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [erro, setErro] = useState("");
  const [loading, setLoading] = useState(false);

  // ✅ Estado do usuário logado (para decidir fluxo)
  const [authUser, setAuthUser] = useState(null);

  const mountedRef = useRef(true);

  const currentUser = useMemo(
    () => authUser || auth.currentUser || null,
    [authUser]
  );

  async function isAdmin(uid) {
    try {
      const ref = doc(db, "admins", uid);
      const snap = await getDoc(ref);

      if (!snap.exists()) return false;

      const data = snap.data() || {};
      // 🔒 Se quiser bloquear um admin, use active:false
      if (data.active === false) return false;

      return true;
    } catch (_) {
      return false;
    }
  }

  // ✅ Observa auth e decide fluxo
  useEffect(() => {
    mountedRef.current = true;

    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!mountedRef.current) return;

      setErro("");
      setAuthUser(user || null);

      if (!user) return;

      const ok = await isAdmin(user.uid);

      // 🔥 Se não for admin, derruba e mostra mensagem
      if (!ok) {
        try {
          await signOut(auth);
        } catch (_) {}

        if (!mountedRef.current) return;

        setAuthUser(null);
        setErro("Você não tem permissão para acessar o painel.");
        return;
      }

      // ✅ Admin OK: entra direto
      navigate("/dashboard", { replace: true });
    });

    return () => {
      mountedRef.current = false;
      unsub();
    };
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (loading) return;

    setErro("");
    setLoading(true);

    try {
      const cred = await signInWithEmailAndPassword(
        auth,
        email.trim(),
        senha.trim()
      );

      setAuthUser(cred.user);

      const ok = await isAdmin(cred.user.uid);

      // 🔥 Se não for admin, derruba
      if (!ok) {
        try {
          await signOut(auth);
        } catch (_) {}

        setAuthUser(null);
        setErro("Você não tem permissão para acessar o painel.");
        setLoading(false);
        return;
      }

      // ✅ Admin OK: vai direto
      navigate("/dashboard", { replace: true });
    } catch (err) {
      const code = err?.code || "";

      if (
        code.includes("auth/invalid-credential") ||
        code.includes("auth/wrong-password")
      ) {
        setErro("E-mail ou senha incorretos.");
      } else if (code.includes("auth/user-not-found")) {
        setErro("Usuário não encontrado.");
      } else if (code.includes("auth/too-many-requests")) {
        setErro("Muitas tentativas. Tente novamente em alguns minutos.");
      } else if (code.includes("auth/network-request-failed")) {
        setErro("Sem conexão. Verifique sua internet e tente novamente.");
      } else {
        setErro("Não foi possível fazer login. Verifique seus dados.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (_) {}
    setAuthUser(null);
    setEmail("");
    setSenha("");
    setErro("");
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <img
          src="/logo_onelove.png"
          alt="One Love Logo"
          className="logo-login"
        />

        <p className="subtitulo">Painel Administrativo</p>

        <form onSubmit={handleLogin}>
          <label>E-mail</label>
          <input
            type="email"
            placeholder="" // ✅ não expõe e-mails
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="username"
          />

          <label>Senha</label>
          <input
            type="password"
            placeholder="••••••"
            value={senha}
            onChange={(e) => setSenha(e.target.value)}
            required
            autoComplete="current-password"
          />

          {erro && <p className="erro">{erro}</p>}

          <button type="submit" className="btn-login" disabled={loading}>
            {loading ? "Entrando..." : "Entrar"}
          </button>

          {/* (Opcional) Botão de sair — útil se já estiver logado e quiser trocar */}
          {currentUser && (
            <button
              type="button"
              className="btn-login"
              onClick={handleLogout}
              style={{ marginTop: 10, opacity: 0.9 }}
            >
              Sair
            </button>
          )}
        </form>
      </div>
    </div>
  );
}
